import { useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Truck, PackageCheck, MessageSquare, Mail, Copy } from "lucide-react";
import { whatsappSend } from "@/lib/pdf";
import { usePinPrompt } from "@/components/app/PinPrompt";

export const PO_CHIP = { draft: "chip-slate", sent: "chip-amber", in_transit: "chip-blue", received: "chip-green", cancelled: "chip-red" };

export const PoStatusChip = ({ status }) => (
  <span className={`chip ${PO_CHIP[status] || "chip-slate"} uppercase text-[10px]`} data-testid="po-status-chip">{(status || "draft").replace("_", " ")}</span>
);

function poMessage(po, link) {
  const lines = (po.lines || []).map((l, i) => `${i + 1}. ${l.name} — ${l.qty} ${l.unit || ""}`).join("\n");
  return `Dear ${po.party_name || "Supplier"},\n\nPlease supply the following against our Purchase Order ${po.number}:\n\n${lines}\n\nOrder value: ${fmtINR(po.total)}\nView / download PDF: ${link}\n\nKindly confirm and share dispatch details.\nThank you.`;
}

export default function PoActions({ row, parties, onChange }) {
  const pinPrompt = usePinPrompt();
  const [link, setLink] = useState("");
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const supplier = parties.find((p) => p._id_str === row.party_id);
  const closed = ["received", "cancelled"].includes(row.po_status);

  const openSend = async () => {
    try {
      const { data } = await api.post(`/txns/${row._id_str}/share`);
      setLink(`${window.location.origin}/share/${data.token}`);
      setEmail(supplier?.email || "");
      setOpen(true);
    } catch (e) { toast.error("Could not create share link"); }
  };

  const markSent = async () => {
    if (row.po_status !== "draft") return;
    await pinPrompt();
    await api.post(`/txns/${row._id_str}/po-status`, { po_status: "sent" }).catch(() => {});
    onChange();
  };

  const sendWa = async () => { whatsappSend(supplier?.phone, poMessage(row, link)); await markSent(); };
  const sendEmail = async () => {
    if (!email) return toast.error("Enter supplier email");
    await pinPrompt();
    try { await api.post(`/txns/${row._id_str}/email-supplier`, { to: email, link }); toast.success(`Emailed ${email}`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
  };

  const inTransit = async () => {
    const transporter = window.prompt("Transporter / vehicle / LR number (optional):", row.transporter || "");
    if (transporter === null) return;
    await pinPrompt();
    try { await api.post(`/txns/${row._id_str}/po-status`, { po_status: "in_transit", transporter }); toast.success("Marked in transit"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const receive = async () => {
    if (!window.confirm("Goods received? This creates a Purchase Bill and adds the items to stock.")) return;
    await pinPrompt();
    try { const { data } = await api.post(`/txns/${row._id_str}/receive`); toast.success(`Received → ${data.number}`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      {!closed && <button className="p-1 hover:bg-emerald-50 text-emerald-700 rounded" title="Send to supplier" onClick={openSend} data-testid={`po-send-${row._id_str}`}><Send size={14} /></button>}
      {!closed && row.po_status !== "in_transit" && <button className="p-1 hover:bg-blue-50 text-blue-700 rounded" title="Mark in transit" onClick={inTransit} data-testid={`po-transit-${row._id_str}`}><Truck size={14} /></button>}
      {!closed && <button className="p-1 hover:bg-emerald-50 text-emerald-700 rounded" title="Receive goods → Purchase bill" onClick={receive} data-testid={`po-receive-${row._id_str}`}><PackageCheck size={14} /></button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="po-send-dialog">
          <DialogHeader><DialogTitle>Send {row.number} to {row.party_name || "supplier"}</DialogTitle></DialogHeader>
          <Label>PDF link (supplier can open without login)</Label>
          <div className="flex gap-2">
            <Input readOnly value={link} data-testid="po-share-link" />
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }}><Copy size={14} /></Button>
          </div>
          <Button className="btn-primary mt-2" onClick={sendWa} data-testid="po-send-wa"><MessageSquare size={14} /> Send on WhatsApp {supplier?.phone ? `(${supplier.phone})` : ""}</Button>
          <div className="border-t border-border pt-3 mt-2">
            <Label>Supplier email</Label>
            <div className="flex gap-2">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="supplier@example.com" data-testid="po-email-input" />
              <Button variant="outline" onClick={sendEmail} data-testid="po-send-email"><Mail size={14} /> Email</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
